// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

/**
 * Shared route building blocks used by both OSS and enterprise routers.
 *
 * IMPORTANT: This file must NOT have an `_enterprise` variant — both
 * router.tsx and router_enterprise.tsx import from here directly.
 */

import { lazy, Suspense } from 'react';
import {
  createRootRoute,
  createRoute,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { AppLayout } from './components/app-layout';
import { useAuthStore } from './stores/auth-store';

// Eagerly loaded routes (critical path: auth + dashboard + core data).
import { LoginPage } from './routes/auth/login';
import { DashboardPage } from './routes/dashboard/index';
import { ExperiencePage } from './routes/experience/index';
import { AccomplishmentsPage } from './routes/accomplishments/index';
import { SkillsPage } from './routes/skills/index';
import { EducationPage } from './routes/education/index';
import { CustomSectionsPage } from './routes/custom-sections/index';

// Eagerly loaded — professional profile is a primary data page.
import { ProfessionalProfilePage } from './routes/profile/index';

// Lazy-loaded routes (secondary pages — loaded on demand).
const ApplicationsPage = lazy(() =>
  import('./routes/applications/index').then((m) => ({ default: m.ApplicationsPage })),
);
const NewApplicationPage = lazy(() =>
  import('./routes/applications/new').then((m) => ({ default: m.NewApplicationPage })),
);
const ApplicationDetailPage = lazy(() =>
  import('./routes/applications/$id').then((m) => ({ default: m.ApplicationDetailPage })),
);
const PromptsPage = lazy(() =>
  import('./routes/prompts/index').then((m) => ({ default: m.PromptsPage })),
);
const ProfileSettingsPage = lazy(() =>
  import('./routes/settings/profile').then((m) => ({ default: m.ProfileSettingsPage })),
);
const AIProviderSettingsPage = lazy(() =>
  import('./routes/settings/ai-provider').then((m) => ({ default: m.AIProviderSettingsPage })),
);
const StorageSettingsPage = lazy(() =>
  import('./routes/settings/storage').then((m) => ({ default: m.StorageSettingsPage })),
);
const TemplatesSettingsPage = lazy(() =>
  import('./routes/settings/templates').then((m) => ({ default: m.TemplatesSettingsPage })),
);
const DangerZoneSettingsPage = lazy(() =>
  import('./routes/settings/danger-zone').then((m) => ({ default: m.DangerZoneSettingsPage })),
);
const NotificationSettingsPage = lazy(() =>
  import('./routes/settings/notifications').then((m) => ({ default: m.NotificationSettingsPage })),
);
const CVPreferencesSettingsPage = lazy(() =>
  import('./routes/settings/cv-preferences').then((m) => ({ default: m.CVPreferencesSettingsPage })),
);
const CvUploadPage = lazy(() =>
  import('./routes/import/cv-upload').then((m) => ({ default: m.CvUploadPage })),
);
const CustomSectionEntriesPage = lazy(() =>
  import('./routes/custom-sections/$sectionId').then((m) => ({ default: m.CustomSectionEntriesPage })),
);

// Minimal loading spinner for lazy route suspense boundaries.
function RouteSpinner() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
    </div>
  );
}

/** Wrap a lazy component in Suspense. */
export function withSuspense(Component: React.LazyExoticComponent<React.ComponentType>) {
  return function LazyRoute() {
    return (
      <Suspense fallback={<RouteSpinner />}>
        <Component />
      </Suspense>
    );
  };
}

// ============================================================
// Shared route definitions
// ============================================================

export const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

export const authLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/login',
  component: LoginPage,
});

/**
 * Creates the app layout route with authentication guard.
 *
 * Accepts an optional `onAuthenticated` callback that runs after the
 * base auth check passes. Enterprise uses this to inject the email
 * verification guard; OSS leaves it unset.
 */
export function createAppLayoutRoute(opts?: {
  onAuthenticated?: (state: { user: ReturnType<typeof useAuthStore.getState>['user'] }) => void;
}) {
  return createRoute({
    getParentRoute: () => rootRoute,
    id: 'app',
    beforeLoad: () => {
      const { isAuthenticated, user } = useAuthStore.getState();
      if (!isAuthenticated) {
        throw redirect({ to: '/auth/login' });
      }
      opts?.onAuthenticated?.({ user });
    },
    component: () => (
      <AppLayout>
        <Outlet />
      </AppLayout>
    ),
  });
}

export const appLayoutRoute = createAppLayoutRoute();

/**
 * Creates all app-layout child routes bound to the given parent route.
 *
 * This factory exists because the enterprise edition creates its own
 * `appLayoutRoute` (with an email-verification guard). If we defined
 * the child routes as static exports referencing the default
 * `appLayoutRoute`, the enterprise router would hit a parent-mismatch
 * error at runtime — TanStack Router requires a route's
 * `getParentRoute` reference to be the exact same instance that it is
 * added to via `addChildren`.
 */
export function createAppRoutes(parent: ReturnType<typeof createAppLayoutRoute>) {
  const dashboardRoute = createRoute({
    getParentRoute: () => parent,
    path: '/dashboard',
    component: DashboardPage,
  });

  const profileRoute = createRoute({
    getParentRoute: () => parent,
    path: '/profile',
    component: ProfessionalProfilePage,
  });

  const experienceRoute = createRoute({
    getParentRoute: () => parent,
    path: '/experience',
    component: ExperiencePage,
  });

  const accomplishmentsRoute = createRoute({
    getParentRoute: () => parent,
    path: '/accomplishments',
    component: AccomplishmentsPage,
  });

  const skillsRoute = createRoute({
    getParentRoute: () => parent,
    path: '/skills',
    component: SkillsPage,
  });

  const educationRoute = createRoute({
    getParentRoute: () => parent,
    path: '/education',
    component: EducationPage,
  });

  const customSectionsRoute = createRoute({
    getParentRoute: () => parent,
    path: '/custom-sections',
    component: CustomSectionsPage,
  });

  const customSectionEntriesRoute = createRoute({
    getParentRoute: () => parent,
    path: '/custom-sections/$sectionId',
    component: withSuspense(CustomSectionEntriesPage),
  });

  const applicationsRoute = createRoute({
    getParentRoute: () => parent,
    path: '/applications',
    component: withSuspense(ApplicationsPage),
  });

  const applicationsNewRoute = createRoute({
    getParentRoute: () => parent,
    path: '/applications/new',
    component: withSuspense(NewApplicationPage),
  });

  const applicationDetailRoute = createRoute({
    getParentRoute: () => parent,
    path: '/applications/$id',
    component: withSuspense(ApplicationDetailPage),
  });

  const promptsRoute = createRoute({
    getParentRoute: () => parent,
    path: '/prompts',
    component: withSuspense(PromptsPage),
  });

  const settingsProfileRoute = createRoute({
    getParentRoute: () => parent,
    path: '/settings/profile',
    component: withSuspense(ProfileSettingsPage),
  });

  const settingsAIProviderRoute = createRoute({
    getParentRoute: () => parent,
    path: '/settings/ai-provider',
    component: withSuspense(AIProviderSettingsPage),
  });

  const settingsStorageRoute = createRoute({
    getParentRoute: () => parent,
    path: '/settings/storage',
    component: withSuspense(StorageSettingsPage),
  });

  const settingsTemplatesRoute = createRoute({
    getParentRoute: () => parent,
    path: '/settings/templates',
    component: withSuspense(TemplatesSettingsPage),
  });

  const settingsDangerZoneRoute = createRoute({
    getParentRoute: () => parent,
    path: '/settings/danger-zone',
    component: withSuspense(DangerZoneSettingsPage),
  });

  const settingsNotificationsRoute = createRoute({
    getParentRoute: () => parent,
    path: '/settings/notifications',
    component: withSuspense(NotificationSettingsPage),
  });

  const settingsCVPreferencesRoute = createRoute({
    getParentRoute: () => parent,
    path: '/settings/cv-preferences',
    component: withSuspense(CVPreferencesSettingsPage),
  });

  const importCvRoute = createRoute({
    getParentRoute: () => parent,
    path: '/import/cv-upload',
    component: withSuspense(CvUploadPage),
  });

  return {
    dashboardRoute,
    profileRoute,
    experienceRoute,
    accomplishmentsRoute,
    skillsRoute,
    educationRoute,
    customSectionsRoute,
    customSectionEntriesRoute,
    applicationsRoute,
    applicationsNewRoute,
    applicationDetailRoute,
    promptsRoute,
    settingsProfileRoute,
    settingsAIProviderRoute,
    settingsStorageRoute,
    settingsTemplatesRoute,
    settingsDangerZoneRoute,
    settingsNotificationsRoute,
    settingsCVPreferencesRoute,
    importCvRoute,
  };
}
